import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { MeditationResults, MeditationResultItem } from '../components/meditation';
import { config } from '../config';
import { authService } from '../services/auth';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import SEO from '../components/common/SEO';

const AVAILABLE_TAGS = [
  'Sitting', 'Lying', 'Walking', 'Eyes open', 'Eyes closed', 'Relaxing',
  'Thinking', 'Focus', 'Sleeping', 'Dreaming', 'Out of body', 'Spiritual', 'Nature',
  'Non-Spiritual', 'Body Scan', 'Story', 'Manifestation', 'Visualization',
  'Affirmations', 'Mantras', 'Healing', 'Music','Ambient','Instrumental',
  'Delta', 'Theta', 'Alpha', 'Beta','Gamma', 
  'Binaural Beats', 'Amplitude Modulation', 'Frequency Modulation',
  'Stereo Modulation', 'Isochronic Tones'
];

const Container = styled.div`
  //max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 2rem;
  color: white;
`;

const SearchContainer = styled.div`
  margin-bottom: 40px;
`;

const SearchRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`;




const TagsSection = styled.div`
  margin-top: 20px;
  margin-bottom: 30px;
`;

const TagsLabel = styled.h3`
  color: #ffffff;
  margin-bottom: 12px;
  font-size: 1rem;
  font-weight: 600;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Tag = styled.button<{ $selected: boolean }>`
  background: ${props => props.$selected ? '#007bff' : '#333'};
  color: white;
  border: 1px solid ${props => props.$selected ? '#007bff' : '#444'};
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$selected ? '#0056b3' : '#444'};
  }
`;


const ResultsSection = styled.div`
  margin-top: 40px;
`;

const NoResultsMessage = styled.div`
  text-align: center;
  color: #999;
  font-size: 1.1rem;
  padding: 60px 20px;
`;

const InfiniteScrollSentinel = styled.div`
  height: 20px;
  margin: 20px 0;
`;

const LoadingIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
  color: #999;
  font-size: 1rem;
`;


const Popular: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [searchResults, setSearchResults] = useState<MeditationResultItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  
  // Infinite scroll state
  const [hasMore, setHasMore] = useState(true);
  const [totalLoaded, setTotalLoaded] = useState(0);
  const [maxResults] = useState(100);
  const [offset, setOffset] = useState(0);

  // Parse URL parameters on mount and restore search state
  useEffect(() => {
    const urlTags = searchParams.get('tags');
    const urlTagsArray = urlTags ? urlTags.split(',').filter(tag => AVAILABLE_TAGS.includes(tag)) : [];
    const urlLoaded = parseInt(searchParams.get('loaded') || '0', 10);

    setSelectedTags(urlTagsArray);

    //automatically start search or restore search results
    setHasSearched(true);
    if (urlLoaded > 0) {
      // Restore previous search results by loading all batches up to urlLoaded
      restoreSearchResults(urlTagsArray, urlLoaded);
    } else {
      // Start fresh search
      performInitialSearch(urlTagsArray);
    }
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount



  const handleTagToggle = (tag: string) => {
    const newTags = selectedTags.includes(tag) 
      ? selectedTags.filter(t => t !== tag)
      : [...selectedTags, tag];
    
    setSelectedTags(newTags);
    // Pass the new tags directly to the search function
    performInitialSearch(newTags);
  };

  // Helper function to build API parameters
  const buildApiParams = (tags: string[], currentOffset: number) => {
    const apiParams = new URLSearchParams();
    
    // Always use popular mode
    apiParams.append('mode', 'popular');
    
    if (tags.length > 0) {
      apiParams.append('tags', tags.join(','));
    }

    apiParams.append('offset', currentOffset.toString());
    apiParams.append('limit', '20');

    return apiParams;
  };

  // Function to update URL parameters
  const updateURLParams = (tags: string[], loaded?: number) => {
    const newParams = new URLSearchParams();
    
    if (tags.length > 0) {
      newParams.set('tags', tags.join(','));
    }

    if (loaded && loaded > 0) {
      newParams.set('loaded', loaded.toString());
    }

    setSearchParams(newParams);
  };

  // Initial search function (resets results)
  const performInitialSearch = useCallback(async (tags: string[]) => {
    setLoading(true);
    setSearchResults([]);
    setOffset(0);
    setTotalLoaded(0);
    setHasMore(true);

    try {
      const apiParams = buildApiParams(tags, 0);
      const response = await fetch(`${config.apiUrl}/scripts/search?${apiParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${authService.getToken() || ''}`
        }
      });

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      const meditations = data.meditations || [];
      
      setSearchResults(meditations);
      setOffset(meditations.length);
      setTotalLoaded(meditations.length);
      setHasMore(data.hasMore && meditations.length < maxResults);

      // Update URL with search results
      updateURLParams(tags, meditations.length);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [maxResults, setSearchParams]);

  // Load more results function for infinite scroll
  const loadMoreResults = useCallback(async () => {
    if (loading || !hasMore || totalLoaded >= maxResults) return;

    setLoading(true);

    try {
      const apiParams = buildApiParams(selectedTags, offset);
      const response = await fetch(`${config.apiUrl}/scripts/search?${apiParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${authService.getToken() || ''}`
        }
      });

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      const newMeditations = data.meditations || [];
      const newTotalLoaded = totalLoaded + newMeditations.length;
      
      setSearchResults(prev => [...prev, ...newMeditations]);
      setOffset(prev => prev + newMeditations.length);
      setTotalLoaded(newTotalLoaded);
      setHasMore(data.hasMore && newTotalLoaded < maxResults);

      // Update URL with new loaded count
      updateURLParams(selectedTags, newTotalLoaded);
    } catch (error) {
      console.error('Load more error:', error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, totalLoaded, maxResults, offset, selectedTags]);

  // Restore search results when returning from a meditation page
  const restoreSearchResults = useCallback(async (tags: string[], targetLoaded: number) => {
    setLoading(true);
    setSearchResults([]);
    setOffset(0);
    setTotalLoaded(0);

    try {
      // Load all results up to targetLoaded in one request
      const apiParams = buildApiParams(tags, 0);
      apiParams.set('limit', Math.min(targetLoaded, maxResults).toString());
      
      const response = await fetch(`${config.apiUrl}/scripts/search?${apiParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${authService.getToken() || ''}`
        }
      });

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      const meditations = data.meditations || [];
      
      setSearchResults(meditations);
      setOffset(meditations.length);
      setTotalLoaded(meditations.length);
      setHasMore(data.hasMore && meditations.length < maxResults);
    } catch (error) {
      console.error('Restore search error:', error);
      setSearchResults([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [maxResults]);

  // Set up infinite scroll
  const { sentinelRef, loadingRef } = useInfiniteScroll({
    loadMore: loadMoreResults,
    hasMore: hasMore && totalLoaded < maxResults,
    loading: loading
  });

  // Create dynamic title and description based on selected tags
  const getPageTitle = () => {
    if (selectedTags.length === 0) return 'Popular Meditations - YouMeditation';
    const tagText = selectedTags.slice(0, 3).join(', ');
    return `Popular ${tagText} Meditations - YouMeditation`;
  };

  const getPageDescription = () => {
    if (selectedTags.length === 0) {
      return 'Discover the most popular meditation scripts on YouMeditation. Find trending guided and unguided meditations loved by our community.';
    }
    const tagText = selectedTags.slice(0, 3).join(', ');
    return `Explore popular ${tagText} meditations. Find the most loved meditation scripts in these categories from our mindful community.`;
  };

  const getPageKeywords = () => {
    const baseKeywords = 'popular meditations, trending meditations, top meditations, YouMeditation';
    if (selectedTags.length === 0) return baseKeywords;
    return `${baseKeywords}, ${selectedTags.join(', ').toLowerCase()}`;
  };

  return (
    <>
      <SEO 
        title={getPageTitle()}
        description={getPageDescription()}
        keywords={getPageKeywords()}
        url="/popular"
      />
      <Container>
      
      <SearchContainer>
        <SearchRow>
          <Title>Popular Meditations</Title>

          
        </SearchRow>

        <TagsSection>
          <TagsLabel>Tags (select to narrow down results):</TagsLabel>
          <TagsContainer>
            {AVAILABLE_TAGS.map(tag => (
              <Tag
                key={tag}
                type="button"
                $selected={selectedTags.includes(tag)}
                onClick={() => handleTagToggle(tag)}
              >
                {tag}
              </Tag>
            ))}
          </TagsContainer>
        </TagsSection>

       
      </SearchContainer>

      <ResultsSection>
        {!hasSearched ? (
          <NoResultsMessage>
            
          </NoResultsMessage>
        ) : (
          <>
           
            
            <MeditationResults 
              meditations={searchResults} 
              loading={false} // Don't show loading state in results grid
            />

            {/* Infinite scroll loading indicator */}
            {loading && searchResults.length > 0 && (
              <LoadingIndicator ref={loadingRef}>
                Loading more meditations...
              </LoadingIndicator>
            )}

            {/* Infinite scroll sentinel */}
            {hasMore && totalLoaded < maxResults && !loading && (
              <InfiniteScrollSentinel ref={sentinelRef} />
            )}

            
          </>
        )}
      </ResultsSection>
    </Container>
    </>
  );
};


export default Popular; 